import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Play } from "lucide-react";

// Parse a video URL into an embeddable form (YouTube / Vimeo / direct .mp4).
const videoEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube-nocookie.com/embed/${yt[1]}?rel=0` };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { type: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.mp4(\?|$)/i.test(url)) return { type: "mp4", src: url };
  return { type: "iframe", src: url };
};

// Accessible video modal. Shows a poster + Play button first (NO autoplay with sound);
// the actual video only loads/plays after the visitor clicks Play. Radix handles focus-trap + Esc.
export const VideoModal = ({
  open, onOpenChange, url, title, description, poster,
  buttonLabel = "Play video", testid = "video-modal",
}) => {
  const [playing, setPlaying] = useState(false);
  useEffect(() => { if (!open) setPlaying(false); }, [open]);
  const embed = videoEmbed(url);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-white border-0" data-testid={testid}>
        {(title || description) ? (
          <DialogHeader className="px-6 pt-6 pb-3 text-left pr-12">
            <DialogTitle className="text-[#1C4358] font-display text-xl">{title || "Video"}</DialogTitle>
            {description
              ? <DialogDescription className="text-[#4B5563] text-[15px] leading-relaxed">{description}</DialogDescription>
              : <DialogDescription className="sr-only">Video</DialogDescription>}
          </DialogHeader>
        ) : (
          <>
            <DialogTitle className="sr-only">{title || "Video"}</DialogTitle>
            <DialogDescription className="sr-only">Video</DialogDescription>
          </>
        )}
        <div className="relative w-full bg-black" style={{ aspectRatio: "16 / 9" }}>
          {!playing ? (
            <button
              type="button"
              onClick={() => embed && setPlaying(true)}
              data-testid={`${testid}-play`}
              aria-label={buttonLabel || "Play video"}
              className="group absolute inset-0 flex items-center justify-center"
            >
              {poster ? (
                <img src={poster} alt={title || "Video preview"} className="absolute inset-0 h-full w-full object-cover" />
              ) : null}
              <span className="absolute inset-0 bg-black/30" />
              <span className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-white/90 text-[#9C1E22] shadow-xl transition-transform group-hover:scale-110">
                <Play size={30} className="ml-1 fill-current" />
              </span>
              {buttonLabel ? (
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-[14px] font-semibold drop-shadow">{buttonLabel}</span>
              ) : null}
            </button>
          ) : embed?.type === "mp4" ? (
            <video src={embed.src} controls autoPlay playsInline className="h-full w-full" data-testid={`${testid}-el`} />
          ) : embed ? (
            <iframe
              src={`${embed.src}${embed.src.includes("?") ? "&" : "?"}autoplay=1`}
              title={title || "Video"}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid={`${testid}-iframe`}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoModal;
